<template>
  <div class="card mb-25 border-0 rounded-0 bg-white add-user-card">
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
      <Form @submit="submitAssign" :validation-schema="assignationSchema">
        <div class="row">
          <!-- Organe -->
          <div class="col-md-6 mb-3">
            <label class="fw-semibold">Organe <span class="text-danger">*</span></label>
            <Field name="organes" v-slot="{ field }">
              <Multiselect
                :options="organesOptions"
                v-model="field.value"
                v-bind="field"
                placeholder="Sélectionner l'organe"
                valueProp="value"
                label="label"
                @change="onOrganeChange"
                :searchable="true"
              />
            </Field>
            <ErrorMessage name="organes" class="text-danger fs-sm-14" />
          </div>

          <!-- Mandature -->
          <div class="col-md-6 mb-3">
            <label class="fw-semibold">Mandature <span class="text-danger">*</span></label>
            <Field name="mandatures" v-slot="{ field }">
              <Multiselect
                :options="mandaturesOptions"
                v-model="field.value"
                v-bind="field"
                placeholder="Sélectionner la mandature"
                valueProp="value"
                label="label"
                :searchable="true"
              />
            </Field>
            <ErrorMessage name="mandatures" class="text-danger fs-sm-14" />
          </div>
        </div>

        <!-- Lignes dynamiques : 2 par ligne -->
        <div v-if="lignes.length > 0" class="mt-4">
          <h5 class="mb-3">Assignation des postes</h5>
          <div class="row g-3">
            <template v-for="(ligne, index) in lignes" :key="index">
              <div class="col-md-6">
                <div class="border rounded p-3 bg-light h-100 d-flex flex-column">
                  <div class="fw-bold text-primary mb-2">{{ ligne.nomPoste }}</div>
                  
                  <div class="mt-auto">
                    <Multiselect
                      :options="filteredMembreOptions(index)"
                      v-model="ligne.membreId"
                      placeholder="Choisir un membre"
                      valueProp="value"
                      label="label"
                      :searchable="true"
                      mode="single"
                      :create-option="true"
                      @create="openCreateModal(index, $event)"
                      class="mb-2"
                    />
                    
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-success w-100"
                      @click="openCreateModal(index)"
                    >
                      + Ajouter un nouveau membre
                    </button>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>

        <!-- Boutons -->
        <div class="mt-4 d-flex gap-3">
          <button type="submit" class="btn btn-success">Valider les assignations</button>
          <router-link to="/liste-assignation" class="btn btn-danger">Annuler</router-link>
        </div>
      </Form>
    </div>
  </div>

  <!-- Modal création membre -->
  <div class="modal fade" id="createMembreModal" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Nouveau membre</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body">
          <div class="mb-3">
            <label class="form-label">Nom <span class="text-danger">*</span></label>
            <input v-model="tempMembre.nom" class="form-control" placeholder="Ex: Diallo" />
          </div>
          <div class="mb-3">
            <label class="form-label">Prénom <span class="text-danger">*</span></label>
            <input v-model="tempMembre.prenom" class="form-control" placeholder="Ex: Mamadou" />
          </div>
          <div class="mb-3">
            <label class="form-label">Photo (facultatif)</label>
            <input type="file" @change="onFileChange" accept="image/*" class="form-control" />
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Annuler</button>
          <button type="button" class="btn btn-primary" @click="confirmCreateMembre">Créer et assigner</button>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import { Form, Field, ErrorMessage } from 'vee-validate';
import * as Yup from 'yup';
import Multiselect from '@vueform/multiselect';
import ApiService from '@/services/ApiService';
import { success, error } from '@/utils/utils';
import router from '@/router';
import { Modal } from 'bootstrap';

interface Poste {
  id: number;
  nomPoste: string;
}

interface OrganeOption {
  value: number;
  label: string;
  postes: Poste[];
}

interface MembreOption {
  value: number;
  label: string;
  nom: string;
  prenom: string;
  file?: File | null;
}

interface LigneAssignation {
  posteId: number;
  nomPoste: string;
  membreId: number | null;
  nouveauMembre?: {
    nom: string;
    prenom: string;
    file?: File | null;
  };
}

export default defineComponent({
  name: 'AddAssignation',
  components: { Form, Field, ErrorMessage, Multiselect },
  setup() {
    const organesOptions = ref<OrganeOption[]>([]);
    const mandaturesOptions = ref<{ value: number; label: string }[]>([]);
    const membresOptions = ref<MembreOption[]>([]); // ← Tous les membres (existants + créés)
    const lignes = ref<LigneAssignation[]>([]);

    const tempIndex = ref<number | null>(null);
    const tempMembre = ref({
      nom: '',
      prenom: '',
      file: null as File | null,
    });

    const modal = ref<Modal | null>(null);

    const assignationSchema = Yup.object().shape({
      organes: Yup.number().required("L'organe est obligatoire"),
      mandatures: Yup.number().required('La mandature est obligatoire'),
    });

    // Chargement des données
    const fetchData = async () => {
      try {
        const [organesRes, mandaturesRes, membresRes] = await Promise.all([
          ApiService.get('/organes?limit=0'),
          ApiService.get('/mandature'),
          ApiService.get('/membre'),
        ]);

        // Organes
        organesOptions.value = organesRes.data.data.data.map((o: any) => ({
          value: o.id,
          label: o.nom,
          postes: o.postes || [],
        }));

        // Mandatures
        mandaturesOptions.value = mandaturesRes.data.data.data.map((m: any) => ({
          value: m.id,
          label: m.libelle,
        }));

        // Membres existants
        membresOptions.value = membresRes.data.map((m: any) => ({
          value: m.id,
          label: `${m.nom} ${m.prenom}`,
          nom: m.nom,
          prenom: m.prenom,
        }));
      } catch (err) {
        error('Erreur de chargement des données');
      }
    };

    const onOrganeChange = (organeId: number) => {
      const organe = organesOptions.value.find(o => o.value === organeId);
      lignes.value = organe ? organe.postes.map(p => ({
        posteId: p.id,
        nomPoste: p.nomPoste,
        membreId: null,
      })) : [];
    };

    // Options filtrées : exclure les membres déjà assignés (sauf temporaires)
    const filteredMembreOptions = (currentIndex: number) => {
      const usedIds = lignes.value
        .filter((_, i) => i !== currentIndex)
        .map(l => l.membreId)
        .filter(id => id !== null && id > 0);

      return membresOptions.value.filter(m => !usedIds.includes(m.value));
    };

    const openCreateModal = (index: number, query = '') => {
      tempIndex.value = index;
      const parts = query.trim().split(' ');
      tempMembre.value = {
        nom: parts[0] || '',
        prenom: parts.slice(1).join(' ') || '',
        file: null,
      };
      modal.value = new Modal('#createMembreModal');
      modal.value.show();
    };

    const onFileChange = (e: Event) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) tempMembre.value.file = file;
    };

    const confirmCreateMembre = () => {
      if (!tempMembre.value.nom || !tempMembre.value.prenom) {
        error('Nom et prénom obligatoires');
        return;
      }

      const newId = -Date.now();
      const newMembre: MembreOption = {
        value: newId,
        label: `${tempMembre.value.nom} ${tempMembre.value.prenom}`,
        nom: tempMembre.value.nom,
        prenom: tempMembre.value.prenom,
        file: tempMembre.value.file || undefined,
      };

      membresOptions.value.push(newMembre);

      if (tempIndex.value !== null) {
        lignes.value[tempIndex.value].membreId = newId;
        lignes.value[tempIndex.value].nouveauMembre = { ...tempMembre.value };
      }

      modal.value?.hide();
      success('Membre créé et assigné !');
    };

    const submitAssign = async (values: any) => {
      if (lignes.value.some(l => !l.membreId)) {
        error('Tous les postes doivent être assignés');
        return;
      }

      const formData = new FormData();
      formData.append('mandatureId', values.mandatures.toString());

      const assignations = lignes.value.map(ligne => {
        const isNew = ligne.membreId! < 0;
        const membre = membresOptions.value.find(m => m.value === ligne.membreId);

        if (isNew && membre?.file) {
          formData.append(`image_${ligne.posteId}`, membre.file, membre.file.name);
        }

        return {
          posteId: ligne.posteId,
          organeId: Number(values.organes), 
          membreId: isNew ? null : ligne.membreId,
          nom: membre?.nom || '',
          prenom: membre?.prenom || '',
        };
      });

      formData.append('assignations', JSON.stringify(assignations));

      try {
        await ApiService.postWithFile('/postmandaturembr', formData);
        success('Assignations enregistrées avec succès !');
        router.push('/listeAssignations');
      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur serveur');
      }
    };

    onMounted(fetchData);

    return {
      assignationSchema,
      organesOptions,
      mandaturesOptions,
      lignes,
      filteredMembreOptions,
      onOrganeChange,
      openCreateModal,
      tempMembre,
      onFileChange,
      confirmCreateMembre,
      submitAssign,
    };
  },
});
</script>